import React, {useContext, useEffect, useState} from 'react';
import styles from './TransactionDetail.module.css';
import usdiIcon from '../static_files/usdi.svg';
import USDIcon from '../static_files/usd.svg';
import completedIcon from '../static_files/Complete.svg'; // <-- New import
import AddEmailModal from '../AddEmailModal/AddEmailModal';
import { persistSkipAddEmailPrompt } from '../AddEmailModal/skipAddEmailPrompt';
import { SwapMainAppContext } from '../../SwapMainAppContext';
import Tooltip from '../Tooltip/Tooltip';

function TransactionDetailModal({
                                    fromAmount,
                                    fromCurrency,
                                    toCurrency,
                                    transactionType,
                                    network,
                                    networkFeePercent,
                                    rate,
                                    eta,
                                    onConfirm,
                                    onClose,
                                    localTime,
                                    onNetworkValuesUpdate,
                                    email,
                                    walletAddress,
                                    skipEmailPrompt = false,
                                    onSkipEmailPromptChange,
                                    confirmed = false,
    setConfirmed,
                                    txsID,
                                    submitErrorTick = 0,
                                }) {
    const { showNotification } = useContext(SwapMainAppContext);
    const [networkFeeAmount, setNetworkFeeAmount] = useState(0);
    const [toAmount, setToAmount] = useState(0);
    const [skipAddEmailPrompt, setSkipAddEmailPrompt] = useState(Boolean(skipEmailPrompt));
    const [showAddEmailModal, setShowAddEmailModal] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);

    useEffect(() => {
        setSkipAddEmailPrompt(Boolean(skipEmailPrompt));
    }, [skipEmailPrompt, walletAddress]);

    useEffect(() => {
        if (confirmed) {
            setIsConfirming(false);
        }
    }, [confirmed]);

    useEffect(() => {
        if (submitErrorTick > 0) {
            setIsConfirming(false);
        }
    }, [submitErrorTick]);

    useEffect(() => {
        async function loadFee() {
            const feePercent = parseFloat(networkFeePercent.replace('%', ''));
            const toAmountTemp = Number(
                (Math.floor(fromAmount * (100 - feePercent)) / 100).toFixed(2)
            );
            setToAmount(toAmountTemp);
            const feeAmount = (fromAmount - toAmountTemp).toFixed(2);
            setNetworkFeeAmount(feeAmount);

            if (onNetworkValuesUpdate) {
                onNetworkValuesUpdate(feeAmount, toAmountTemp, feePercent, eta);
            }
        }

        loadFee();
    }, [fromAmount, networkFeePercent, onNetworkValuesUpdate, eta]);

    // Called on the main "Confirm" (or "Check Status") button
    const handleConfirmClick = () => {
        if (confirmed) {
            if (!txsID) {
                window.location.href = `/transaction`;
            } else {
                window.location.href = `/transaction?txId=${txsID}`;
            }
            return;
        }
        if (isConfirming) {
            return;
        }
        if (!skipAddEmailPrompt && walletAddress && (!email || email === '--')) {
            setShowAddEmailModal(true);
        } else {
            setIsConfirming(true);
            onConfirm();
        }
    };

    /**
     * Close the AddEmailModal
     * @param {boolean} skipPrompt - whether to skip showing again
     */
    const persistSkipEmailPreference = async (skipPrompt) => {
        if (!skipPrompt) {
            return;
        }

        const persisted = await persistSkipAddEmailPrompt(walletAddress);
        if (persisted) {
            setSkipAddEmailPrompt(true);
            onSkipEmailPromptChange?.(true);
            return;
        }

        showNotification('error', 'Failed to save your preference. Please try again.');
    };

    const handleCloseModal = async (skipPrompt) => {
        setShowAddEmailModal(false);
        await persistSkipEmailPreference(skipPrompt);
    };

    const handleNoThanks = async (skipPrompt) => {
        setShowAddEmailModal(false);
        await persistSkipEmailPreference(skipPrompt);
        if (isConfirming) {
            return;
        }
        setIsConfirming(true);
        onConfirm();
    };

    const handleAddEmailSuccess = (skipPrompt) => {
        setShowAddEmailModal(false);
        if (skipPrompt) {
            setSkipAddEmailPrompt(true);
            onSkipEmailPromptChange?.(true);
        }
        if (isConfirming) {
            return;
        }
        setIsConfirming(true);
        onConfirm();
    };

    const handleCloseTransactionDetail = () => {
        setIsConfirming(false);
        if (confirmed) {
            setConfirmed(false);
            onClose();
        } else {
            onClose();
        }
    };

    const isConfirmButtonDisabled = !confirmed && isConfirming;
    const confirmButtonLabel = confirmed
        ? 'Check Status in Transactions'
        : isConfirmButtonDisabled
            ? 'Confirming...'
            : 'Confirm';
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCloseTransactionDetail()
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>

            {/* Show the Transaction Detail only if AddEmailModal is NOT open */}
            {!showAddEmailModal && (
                <div className={styles.transactionDetailModal}>
                    <div className={styles.modalHeader}>
                        <h2>Transaction detail</h2>
                        <div
                            className={styles.closeBtn}
                            onClick={handleCloseTransactionDetail}
                            aria-label="Close"
                        >
                            ×
                        </div>
                    </div>

                    <div className={styles.lineWrapper}>
                        <div className={styles.dividerLineFull}></div>
                    </div>

                    <div className={styles.modalBody}>
                        <div className={styles.statusSection}>
                            <div className={styles.statusImageContainer}>
                                {confirmed ? (
                                    <img
                                        src={completedIcon}
                                        alt="Status complete"
                                        className={styles.statusIcon}
                                    />
                                ) : (
                                    <div
                                        className={styles.statusProgressIndicator}
                                        aria-label="Status in progress"
                                    >
                                        <div className={styles.statusProgressRing} />
                                        <div className={styles.statusProgressSpinner} />
                                    </div>
                                )}
                            </div>
                            <div className={styles.statusText}>
                                {confirmed ? 'Completed' : 'In progress'}
                            </div>
                        </div>

                        <div className={styles.transactionAmounts}>
                            <div className={styles.transactionFrom}>
                                <span className={styles.label}>From</span>
                                <div className={styles.amountRow}>
                                    <img src={usdiIcon} alt="From currency"/>
                                    <span className={styles.amount}>
                    -{fromAmount} {fromCurrency}
                  </span>
                                </div>
                            </div>

                            <div className={styles.transactionTo}>
                                <span className={styles.label}>To</span>
                                <div className={styles.amountRow}>
                                    <img src={USDIcon} alt="To currency"/>
                                    <span className={styles.greenAmount}>
                    +{Number(toAmount.toFixed(2))} {toCurrency}
                  </span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.transactionDetails}>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Transaction type</span>
                                <span className={styles.detailValue}>{transactionType}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Time</span>
                                <span className={styles.detailValue}>{localTime}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Network</span>
                                <Tooltip
                                    text={
                                        <>
                                            A decentralized crypto-to-cash network
                                            <br/>
                                            for on-ramp and off-ramp transactions
                                        </>
                                    }
                                >
                  <span
                      style={{cursor: 'pointer', borderBottom: '1.4px dotted #797979'}}
                      className={styles.detailValue}
                  >
                    {network}
                  </span>
                                </Tooltip>
                            </div>
                            <div className={styles.detailRow}>
                                <Tooltip
                                    text={
                                        <>
                                            The total amount charged by the C2C
                                            <br/>
                                            network for processing this order
                                        </>
                                    }
                                >
                  <span
                      style={{cursor: 'pointer', borderBottom: '1.4px dotted #797979'}}
                      className={styles.detailLabel}
                  >
                    Network fee ({networkFeePercent})
                  </span>
                                </Tooltip>
                                <span className={styles.detailValue}>
                  {networkFeeAmount} USDi
                </span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Rate</span>
                                <span className={styles.detailValue}>{rate}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>ETA</span>
                                <span className={styles.detailValue}>{eta}</span>
                            </div>
                        </div>

                        <div className={styles.lineWrapper}>
                            <div className={styles.dividerLineFull}></div>
                        </div>

                        <div className={styles.transactionNotes}>
                            <p>
                                Funds stay in a secure vault until the operator’s collateral is
                                locked. Funds are released only after the collateral is secured.&nbsp;
                                <a
                                    href="https://docs.kira.trading/kira-club/c2c-network/c2c-redemption-process"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{color: '#6CE3C4', textDecoration: 'underline', fontSize: '10px'}}
                                >
                                    Learn more
                                </a>.
                            </p>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            onClick={handleConfirmClick}
                            className={styles.confirmBtn}
                            style={confirmed ? {fontSize: '16px'} : undefined}
                            disabled={isConfirmButtonDisabled}
                        >
                            {confirmButtonLabel}
                        </button>
                    </div>
                </div>
            )}

            {showAddEmailModal && (
                <AddEmailModal
                    walletAddress={walletAddress}
                    onClose={handleCloseModal}
                    onNoThanks={handleNoThanks}
                    onSuccess={handleAddEmailSuccess}
                />
            )}
        </div>
    );
}

export default TransactionDetailModal;